import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Check, Sparkles, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { PlanPaymentSelector } from '@/components/plans/PlanPaymentSelector';
import { SubscriptionPlan } from '@/hooks/usePlans';

type BillingPeriod = 'monthly' | 'annual';

export function PlanSelectionOnboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { tenant } = useUserProfile();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPaymentSelector, setShowPaymentSelector] = useState(false);
  const [planForPayment, setPlanForPayment] = useState<SubscriptionPlan | null>(null);

  const { data: plans, isLoading } = useQuery({
    queryKey: ['onboarding_plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as SubscriptionPlan[];
    },
  });

  const createTenantMutation = useMutation({
    mutationFn: async (planId: string) => {
      if (!user) throw new Error('Usuário não autenticado');
      
      console.log('🔄 Atualizando plano do tenant:', planId);
      
      // Buscar tenant existente (já criado pela trigger handle_new_user)
      const { data: role, error: roleError } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .single();
      
      if (roleError || !role?.tenant_id) {
        console.error('❌ Erro ao buscar tenant:', roleError);
        throw new Error('Tenant não encontrado. Entre em contato com o suporte.');
      }
      
      console.log('✅ Tenant encontrado:', role.tenant_id);
      
      // Atualizar apenas o plano do tenant existente
      const { error: updateError } = await supabase
        .from('tenants')
        .update({ subscription_plan_id: planId })
        .eq('id', role.tenant_id);
      
      if (updateError) {
        console.error('❌ Erro ao atualizar plano:', updateError);
        throw updateError;
      }
      
      console.log('✅ Plano atualizado com sucesso');
      return { id: role.tenant_id };
    },
    onSuccess: () => {
      // Invalidar queries na ordem correta
      queryClient.invalidateQueries({ queryKey: ['user_roles'] });
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['current_subscription_plan'] });
      
      // Forçar refetch
      queryClient.refetchQueries({ queryKey: ['user_roles'] });
      
      toast.success('Plano selecionado com sucesso! Bem-vindo ao Dashi! 🎉');
      setShowConfirmDialog(false);
    },
    onError: (error) => {
      toast.error('Erro ao criar conta: ' + error.message);
    },
  });

  const getPrice = (plan: SubscriptionPlan) => {
    return billingPeriod === 'monthly' ? plan.price_monthly : plan.price_annual;
  };

  const getPriceLabel = () => {
    return billingPeriod === 'monthly' ? '/mês' : '/ano';
  };

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    const isFree = plan.price_monthly === 0 && plan.price_annual === 0 && plan.price_lifetime === 0;
    const hasStripeIds = plan.stripe_price_monthly_id || plan.stripe_price_annual_id || plan.stripe_price_lifetime_id;

    if (isFree) {
      setSelectedPlan(plan);
      setShowConfirmDialog(true);
    } else if (hasStripeIds) {
      setPlanForPayment(plan);
      setShowPaymentSelector(true);
    } else {
      toast.info('Entre em contato para contratar este plano');
    }
  };

  const handleConfirmChange = () => {
    if (selectedPlan) {
      createTenantMutation.mutate(selectedPlan.id);
    }
  };

  return (
    <>
      {/* Modal não-fechável */}
      <Dialog open={true} modal={true}>
        <DialogContent 
          className="max-w-[95vw] lg:max-w-[1400px] max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-3xl text-center">
              🎉 Bem-vindo ao Dashi!
            </DialogTitle>
            <DialogDescription className="text-center space-y-2">
              <div className="flex items-center justify-center gap-2 mt-2">
                <AlertCircle className="h-5 w-5 text-primary" />
                <span className="text-base">Escolha seu plano para começar a usar</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                Seu plano atual: 
                <Badge variant="secondary" className="ml-1">
                  Nenhum plano ativo
                </Badge>
              </div>
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-6 mt-4">
              {/* Billing Period Toggle */}
              <div className="flex justify-center">
                <Tabs value={billingPeriod} onValueChange={(value) => setBillingPeriod(value as BillingPeriod)}>
                  <TabsList className="grid w-full grid-cols-2 max-w-md">
                    <TabsTrigger value="monthly">Mensal</TabsTrigger>
                    <TabsTrigger value="annual">
                      Anual
                      <Badge variant="secondary" className="ml-2">-20%</Badge>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Pricing Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {plans?.map((plan, index) => {
                  const price = getPrice(plan);
                  const isPopular = index === 1; // Plano do meio como popular
                  
                  return (
                    <Card 
                      key={plan.id} 
                      className={`relative ${isPopular ? 'border-primary shadow-xl scale-105' : 'border-2'}`}
                    >
                      {isPopular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <Badge className="gap-1">
                            <Sparkles className="h-3 w-3" />
                            Mais Popular
                          </Badge>
                        </div>
                      )}

                      <CardHeader className="space-y-4">
                        <CardTitle className="text-2xl">{plan.name}</CardTitle>
                        <div className="space-y-2">
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-bold">
                              R$ {price?.toFixed(2)}
                            </span>
                            <span className="text-muted-foreground">
                              {getPriceLabel()}
                            </span>
                          </div>
                          {billingPeriod === 'annual' && plan.price_monthly > 0 && (
                            <p className="text-sm text-muted-foreground">
                              Economize R$ {((plan.price_monthly * 12) - plan.price_annual).toFixed(2)}/ano
                            </p>
                          )}
                        </div>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        <div className="space-y-3">
                          <div className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                            <span>
                              {plan.max_users === 0 ? 'Usuários ilimitados' : `Até ${plan.max_users} usuário(s)`}
                            </span>
                          </div>

                          <div className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                            <span>
                              {(plan as any).max_products === 0 ? 'Produtos ilimitados' : `Até ${(plan as any).max_products} produto(s)`}
                            </span>
                          </div>

                          <div className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                            <span>
                              {plan.max_integrations === 0 ? 'Integrações ilimitadas' : `Até ${plan.max_integrations} integração(ões)`}
                            </span>
                          </div>

                          {plan.basic_metrics && (plan.basic_metrics as string[]).length > 0 && (
                            <div className="flex items-start gap-2">
                              <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                              <span>Métricas básicas incluídas</span>
                            </div>
                          )}

                          {plan.advanced_metrics_enabled && (
                            <div className="flex items-start gap-2">
                              <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                              <span>Métricas avançadas</span>
                            </div>
                          )}

                          {plan.goals_enabled && (
                            <div className="flex items-start gap-2">
                              <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                              <span>
                                {plan.max_goals === 0 ? 'Metas ilimitadas' : `Até ${plan.max_goals} meta(s)`}
                              </span>
                            </div>
                          )}

                          {plan.manual_entry_enabled && (
                            <div className="flex items-start gap-2">
                              <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                              <span>Lançamento manual</span>
                            </div>
                          )}

                          {plan.bulk_import_enabled && (
                            <div className="flex items-start gap-2">
                              <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                              <span>Importação em massa</span>
                            </div>
                          )}

                          {plan.priority_support && (
                            <div className="flex items-start gap-2">
                              <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                              <span>Suporte prioritário</span>
                            </div>
                          )}
                        </div>
                      </CardContent>

                      <CardFooter>
                        <Button
                          className="w-full"
                          variant={isPopular ? 'default' : 'outline'}
                          onClick={() => handleSelectPlan(plan)}
                          disabled={createTenantMutation.isPending}
                        >
                          Selecionar Plano
                        </Button>
                      </CardFooter>
                    </Card>
                  );
                })}
              </div>

              {/* Ver Comparação Detalhada */}
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => window.open('/pricing-details', '_blank')}
                  className="gap-2"
                >
                  Ver comparação detalhada dos planos
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar seleção de plano</AlertDialogTitle>
            <AlertDialogDescription>
              Você está prestes a selecionar o plano <strong>{selectedPlan?.name}</strong>.
              {selectedPlan && (
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <div className="text-sm space-y-1">
                    <div>Valor mensal: <strong>R$ {selectedPlan.price_monthly.toFixed(2)}</strong></div>
                    {selectedPlan.price_annual > 0 && (
                      <div>Valor anual: <strong>R$ {selectedPlan.price_annual.toFixed(2)}</strong></div>
                    )}
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmChange} disabled={createTenantMutation.isPending}>
              {createTenantMutation.isPending ? 'Criando conta...' : 'Confirmar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment Selector */}
      {planForPayment && (
        <PlanPaymentSelector
          open={showPaymentSelector}
          onOpenChange={setShowPaymentSelector}
          plan={planForPayment}
        />
      )}
    </>
  );
}
